/**
 * Every address on the site, in one place.
 *
 * The site is a set of real paths, one folder per plan type, so a second type is a
 * second folder rather than a rearrangement:
 *
 *     /                      the landing page: one card per plan type
 *     /sheds/                the shed catalog: the ready-made designs
 *     /sheds/<slug>/         one ready-made design
 *     /sheds/design/         the shed configurator
 *     /sheds/reference/      the shed reference and glossary
 *     /sheds/film/           the shed film: thirty seconds from plan to shed, drawn
 *     /sheds/film/workshop/  the workshop film: from the kitchen table to a shop of one's own
 *     /sheds/how-to/<name>/  a how-to: one technique explained, drawn the way the films are
 *     /terms/                the terms of sale, for every plan type
 *     /plans/                what this browser has bought, and a payment coming back
 *
 * The static pages are written at build time (`site/build.ts`); the configurator, the
 * plans page and a plan type's film are the interactive pages, each its own HTML entry
 * (`vite.config.ts`).
 * An interactive page keeps its own state after a `#` — `#/?preset=…`, `#/purchase?…` —
 * because what comes after the `#` never leaves the browser (`routing.ts`).
 *
 * The two addresses the edge also writes, into Stripe's return URLs, are defined in
 * `@planforge/api/wire` and re-exported here, so there is one copy of each.
 *
 * Plain values with no imports but the wire's constants, so the page builders, the
 * configurator and the Vite config can all read it.
 */

import { PLANS_PATH, SHED_DESIGNER_PATH } from "@planforge/api/wire";

export { PLANS_PATH, SHED_DESIGNER_PATH };

/** The landing page. */
export const HOME_PATH = "/";

/** The shed catalog: every ready-made design, with a way into the configurator. */
export const SHED_CATALOG_PATH = "/sheds/";

/** The shed reference and glossary (`reference/`). */
export const SHED_REFERENCE_PATH = "/sheds/reference/";

/** The shed film (`film/`): a drawn short that plays on its own page and records as an ad. */
export const SHED_FILM_PATH = "/sheds/film/";

/** The shed's second film: a workshop, for somebody with no room to make things. */
export const SHED_WORKSHOP_FILM_PATH = "/sheds/film/workshop/";

/**
 * The shed's first how-to (`film/floorHowTo.ts`): framing a floor, and skids vs. piers vs. a
 * slab. A how-to is drawn with the films' kit and played by their page, but it teaches
 * one technique for minutes rather than selling the plans in thirty seconds, so the how-tos
 * have a folder of their own beside the films'.
 */
export const SHED_FLOOR_HOW_TO_PATH = "/sheds/how-to/frame-a-shed-floor/";

/** The terms of sale (`legal/`), the same for every plan type. */
export const TERMS_PATH = "/terms/";

/** One ready-made shed's page. */
export function presetPath(slug: string): string {
  return `${SHED_CATALOG_PATH}${slug}/`;
}

/**
 * Names a ready-made design may not take, because the catalog's folder already uses
 * them for a page of its own. `test/presets.test.ts` holds the catalog to it.
 */
export const RESERVED_SLUGS: readonly string[] = ["design", "reference", "film", "how-to"];

/** The configurator, opened on one of the ready-made designs (`presets/catalog.ts`). */
export function presetAddress(slug: string): string {
  return `${SHED_DESIGNER_PATH}#/?preset=${encodeURIComponent(slug)}`;
}

/** The plans page, on the screen a payment comes back to (`purchase/PurchaseReturn.tsx`). */
export function purchaseAddress(checkoutId: string): string {
  return `${PLANS_PATH}#/purchase?checkout=${encodeURIComponent(checkoutId)}`;
}

/** A section of the terms, by its anchor (`legal/terms.ts`). */
export function termsSection(id: string): string {
  return `${TERMS_PATH}#${id}`;
}
